import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/lib/auth.jsx';

export default function SignupPage() {
    const [form, setForm] = useState({ full_name: '', company_name: '', email: '', password: '' });
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { signup } = useAuth();

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.password || !form.full_name) {
            toast.error('Please complete all required fields.');
            return;
        }
        if (form.password.length < 8) {
            toast.error('Password must be at least 8 characters.');
            return;
        }
        // Mirror the backend requirement so we catch it before hitting the API.
        if (!/[a-zA-Z]/.test(form.password) || !/[0-9]/.test(form.password)) {
            toast.error('Password must contain at least one letter and one number.');
            return;
        }
        if (!agreed) {
            toast.error('Please accept the Terms of Service and Privacy Policy to continue.');
            return;
        }
        setLoading(true);
        try {
            await signup(form);
            toast.success('Account created. Start your 7-day free trial to unlock the app.');
            navigate('/pricing');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            <div className="hidden lg:flex relative qf-hero-bg qf-noise items-end p-12">
                <div className="relative z-10">
                    <Logo size="lg" />
                    <h2 className="mt-6 text-4xl font-display tracking-tight max-w-md">Start filling your pipeline today.</h2>
                    <p className="mt-3 text-slate-600 max-w-md">Sign up, pick a plan, and unlock your first leads in under 2 minutes.</p>
                    <ul className="mt-6 space-y-2 text-sm text-slate-700">
                        <li className="flex items-center gap-2">✓ Real public property data via RentCast</li>
                        <li className="flex items-center gap-2">✓ NOAA storm overlay</li>
                        <li className="flex items-center gap-2">✓ 8-section quote builder with PDF + e-sign</li>
                    </ul>
                </div>
            </div>
            <div className="flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden inline-block mb-6"><Logo /></Link>
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-display text-2xl">Create your account</CardTitle>
                            <p className="text-sm text-slate-500">Sign up in seconds. Next step: start your 7-day free trial (card required).</p>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label htmlFor="full_name">Your name</Label>
                                    <Input id="full_name" required value={form.full_name} onChange={set('full_name')} data-testid="signup-fullname-input" />
                                </div>
                                <div>
                                    <Label htmlFor="company_name">Company name (optional)</Label>
                                    <Input id="company_name" value={form.company_name} onChange={set('company_name')} data-testid="signup-company-input" />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input id="email" type="email" required value={form.email} onChange={set('email')} data-testid="signup-email-input" />
                                </div>
                                <div>
                                    <Label htmlFor="password">Password</Label>
                                    <Input id="password" type="password" required value={form.password} onChange={set('password')} data-testid="signup-password-input" />
                                    <p className="text-[11px] text-slate-500">At least 8 characters, including one letter and one number.</p>
                                </div>
                                <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={agreed}
                                        onChange={(e) => setAgreed(e.target.checked)}
                                        className="mt-1 h-4 w-4 accent-amber-500 cursor-pointer"
                                        data-testid="signup-tos-checkbox"
                                        required
                                    />
                                    <span>
                                        I agree to the <Link to="/terms" target="_blank" className="text-slate-900 underline" data-testid="signup-tos-link">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-slate-900 underline" data-testid="signup-privacy-link">Privacy Policy</Link>, including the AI estimation disclaimer and my responsibility to comply with all applicable laws when contacting homeowners.
                                    </span>
                                </label>
                                <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="signup-submit-button">
                                    {loading ? 'Creating…' : 'Create account'}
                                </Button>
                                <p className="text-center text-sm text-slate-600">
                                    Have an account? <Link to="/login" className="text-slate-900 font-medium underline" data-testid="signup-to-login-link">Sign in</Link>
                                </p>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
